import React, { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useRecoilState } from "recoil"
import ClockSVG from "src/assets/icons/clock.svg"
import EventTypeSVG from "src/assets/icons/event_type.svg"
import LocationSVG from "src/assets/icons/location.svg"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { useLoginContext } from "src/hooks/useLoginContext"
import {
  APPLICATION_STATUS,
  EVENT_STATUS,
  EVENT_TYPE,
  MEMBER_TYPE,
  RESPONSE_CODE,
  SNACKBAR_TYPES,
} from "src/modules/const"
import { getLabel } from "src/modules/helper"
import { Event, UserApplication, EventService } from "src/openapi"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "src/states/showToast"
import { Box, Button, Image, Modal, Typography } from "src/UILibrary"
import { EventBehalfJoinModal } from "./EventBehalfJoinModal"
import "./event_item.scss"

type Prop = {
  item?: Event
  open: boolean
  handleOpen: any
  refreshEvents?: any
}

export const EventJoinModal = ({ item, open, handleOpen, refreshEvents }: Prop) => {
  const { t } = useTranslation("translation", { keyPrefix: "event" })

  const navigate = useNavigate()

  const { loggedin, areas, accessToken, checkLoginStatus, awsID, userType } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [online, setOnline] = useState<string>()

  const [joint, setJoint] = useState<boolean>(false)
  const [cancelled, setCancelled] = useState<boolean>(false)
  const [application, setApplication] = useState<UserApplication>()

  const [joinType, setJoinType] = useState<string>()

  const [openBehalfDlg, setOpenBehalfDlg] = useState<boolean>(false)

  useEffect(() => {
    if (!item) {
      return
    }

    setOnline(item.type as string)
    if (item.type === EVENT_TYPE.BOTH) {
      setOnline(EVENT_TYPE.ONLINE)
    }

    if (item.eventApplications) {
      for (const application of item.eventApplications) {
        if (application.application?.userId === awsID) {
          setApplication({
            id: application.application.id,
            eventId: item.id,
            userId: application.application.userId,
            userType: userType?.toString(),
            type: application.application.type,
            status: application.application.status,
            rejectReason: application.application.rejectReason,
            event: item,
            createdAt: application.application.createdAt,
            updatedAt: application.application.updatedAt,
          })
          if (application.application.status === APPLICATION_STATUS.RESERVED) {
            setJoint(true)
            setJoinType(application.application.type)
          } else {
            setJoint(false)
            setCancelled(true)
          }
          return
        }
      }
    }
    setJoint(false)
    setJoinType(undefined)
  }, [item, awsID])

  if (!item) {
    return <></>
  }

  const handleJoin = () => {
    if (!online) {
      setShowToast({ show: true, text: t("select_join_type"), type: SNACKBAR_TYPES.WARNING })
      return
    }

    if (getApplicationCount() >= item.applicationMax!) {
      setShowToast({ show: true, text: t("join_behalf_limit"), type: SNACKBAR_TYPES.ERROR })
      return
    }

    setShowProgress(true)
    EventService.createEventApplication(accessToken, item.id as number, { type: online })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          setShowToast({ show: true, text: t("success_join"), type: SNACKBAR_TYPES.SUCCESS })
          handleOpen(false)
          refreshEvents()
        } else {
          setShowToast({ show: true, text: t("failed_join"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_join"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleOpenBehalfJoinDlg = () => {
    handleOpen(false)
    setOpenBehalfDlg(true)
  }

  const handleRejoin = () => {
    if (getApplicationCount() >= item.applicationMax!) {
      setShowToast({ show: true, text: t("join_behalf_limit"), type: SNACKBAR_TYPES.ERROR })
      return
    }

    setShowProgress(true)
    EventService.updateEventApplication(accessToken, item.id as number, application?.id as number, {
      status: APPLICATION_STATUS.RESERVED,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_join"), type: SNACKBAR_TYPES.SUCCESS })
          handleOpen(false)
          refreshEvents()
        } else {
          setShowToast({ show: true, text: t("failed_join"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_join"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleEventLink = () => {
    if (!item.eventApplications) {
      return
    }

    if (item.status === EVENT_STATUS.STARTED && joinType === EVENT_TYPE.ONLINE) {
      for (const application of item.eventApplications) {
        if (application.application?.userId === awsID) {
          window.open(item.linkUrl as string, "_blank")
        }
      }
    }
  }

  const getEventTypeString = () => {
    if (item.status === EVENT_STATUS.STARTED) {
      if (joinType === EVENT_TYPE.ONLINE) {
        return item.linkUrl
      }
    }

    if (item.type === EVENT_TYPE.ONLINE) {
      return t("zoom_open")
    } else if (item.type === EVENT_TYPE.OFFLINE) {
      return t("offline")
    } else {
      return `${t("online")}/${t("zoom_open")}`
    }
  }

  const getApplicationCount = () => {
    if (!item.eventApplications) {
      return 0
    }

    let count = 0
    for (const application of item.eventApplications) {
      if (application.application?.status === APPLICATION_STATUS.RESERVED) {
        count++
      }
    }
    return count
  }

  const handleDetail = () => {
    navigate("mypage/inbox/event", { state: { application: application } })
  }
  return (
    <>
      <Modal
        open={open}
        onClose={() => handleOpen(false)}
        sx={{ height: "auto" }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          width="sm"
          display="flex"
          flexDirection="column"
          sx={{ borderRadius: "10px", bgcolor: "white", borderColor: "white" }}
        >
          <Box width="100%" sx={{ p: "10px" }}>
            {item.status === EVENT_STATUS.STARTED && (
              <Box sx={{ px: "10px", py: "6px", bgcolor: "#FFB800", position: "absolute" }}>
                <Typography.ExtraSmallText sx={{ color: "white" }}>
                  {t("started")}
                </Typography.ExtraSmallText>
              </Box>
            )}
            {item.status === EVENT_STATUS.RECEIVING && (
              <Box sx={{ px: "10px", py: "6px", bgcolor: "#ED8C81", position: "absolute" }}>
                <Typography.ExtraSmallText sx={{ color: "white" }}>
                  {t("receiving")}
                </Typography.ExtraSmallText>
              </Box>
            )}
            {item.status === EVENT_STATUS.ARCHIVED && (
              <Box sx={{ px: "10px", py: "6px", bgcolor: "#235496", position: "absolute" }}>
                <Typography.ExtraSmallText sx={{ color: "white" }}>
                  {t("archived")}
                </Typography.ExtraSmallText>
              </Box>
            )}
            <Image
              src={item.imageUrl as string}
              sx={{ width: "100%", maxHeight: "300px", bgcolor: "divider" }}
              alt=""
              imageStyle={{ width: "100%", height: "100%" }}
            />
            <Typography.DetailText
              sx={{
                mt: "20px",
                color: "#1F6757",
                fontFamily: "Noto Sans JP !important",
                fontWeight: "700",
              }}
            >
              {t("event_title")}
            </Typography.DetailText>
            <Typography.Large className="font-large">{item.title}</Typography.Large>
            <Typography.DetailText
              sx={{
                mt: "15px",
                color: "#1F6757",
                fontFamily: "Noto Sans JP !important",
                lineHeight: "14px",
                fontWeight: "700",
              }}
            >
              {t("detail")}
            </Typography.DetailText>
            <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", mt: "10px" }}>
              {item.description}
            </Typography.DetailText>
            <Box display="flex" sx={{ mt: "20px" }}>
              <Image src={ClockSVG} sx={{ lineHeight: "16px" }} />
              <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", ml: "5px" }}>
                {item.startTime?.slice(0, 16).replace("T", " ")}
              </Typography.DetailText>
            </Box>
            <Box display="flex" sx={{ mt: "10px" }}>
              <Image src={LocationSVG} sx={{ lineHeight: "16px" }} />
              <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important", ml: "5px" }}>
                {getLabel(areas, item.areaId as number)}
              </Typography.DetailText>
            </Box>
            <Box display="flex" sx={{ mt: "10px" }}>
              <Image src={EventTypeSVG} sx={{ lineHeight: "16px" }} />
              <Typography.DetailText
                sx={{
                  fontFamily: "Noto Sans JP !important",
                  ml: "10px",
                  ...(joinType === EVENT_TYPE.ONLINE
                    ? { ":hover": { opacity: 0.8, cursor: "pointer" } }
                    : {}),
                }}
                onClick={() => handleEventLink()}
              >
                {getEventTypeString()}
              </Typography.DetailText>
            </Box>
            {item.status !== EVENT_STATUS.ARCHIVED && (
              <Box display="flex" sx={{ mt: "10px" }}>
                <Typography.DetailText sx={{ fontFamily: "Noto Sans JP !important" }}>
                  {`定員 ${getApplicationCount()}名/${item.applicationMax}名`}
                </Typography.DetailText>
              </Box>
            )}
          </Box>
          {item.status === EVENT_STATUS.ARCHIVED ? (
            <OutlinedButton
              caption={t("to_archive_url")}
              onClick={() => window.open(item.archiveVideoUrl as string, "_blank")}
              sx={{ mt: "20px", mx: "auto", color: "primaryMain" }}
              fontColor="primary.main"
              fontWeight="700"
            />
          ) : loggedin ? (
            <>
              {(userType === MEMBER_TYPE.USER || userType === MEMBER_TYPE.ADVISER) && (
                <>
                  {!joint && item.type === EVENT_TYPE.BOTH && (
                    <>
                      <Typography.DetailText
                        sx={{ fontFamily: "Noto Sans JP !important", mt: "20px" }}
                      >
                        {t("select_how_to_request")}
                      </Typography.DetailText>
                      <CheckBoxGroupField
                        radio
                        datas={[
                          { label: t("join_online"), value: EVENT_TYPE.ONLINE },
                          { label: t("join_offline"), value: EVENT_TYPE.OFFLINE },
                        ]}
                        selected={online}
                        handleSelected={setOnline}
                        labelSize={14}
                      />
                    </>
                  )}
                  {joint && (
                    <OutlinedButton
                      caption={t("joint")}
                      onClick={() => handleDetail()}
                      sx={{ mt: "20px", mx: "auto", color: "primaryMain" }}
                      fontColor="primary.main"
                      fontWeight="700"
                    />
                  )}
                  {!joint && !cancelled && (
                    <OutlinedButton
                      caption={t("request_join")}
                      onClick={() => handleJoin()}
                      sx={{ mt: "20px", mx: "auto", color: "primaryMain" }}
                      fontColor="primary.main"
                      fontWeight="700"
                    />
                  )}
                  {!joint && cancelled && (
                    <OutlinedButton
                      caption={t("request_rejoin")}
                      onClick={() => handleRejoin()}
                      sx={{ mt: "20px", mx: "auto", color: "primaryMain" }}
                      fontColor="primary.main"
                      fontWeight="700"
                    />
                  )}
                </>
              )}
              {userType === MEMBER_TYPE.ADMINISTRATOR && (
                <>
                  {!joint && item.type === EVENT_TYPE.BOTH && (
                    <>
                      <Typography.DetailText
                        sx={{ fontFamily: "Noto Sans JP !important", mt: "20px" }}
                      >
                        {t("select_how_to_request")}
                      </Typography.DetailText>
                      <CheckBoxGroupField
                        radio
                        datas={[
                          { label: t("join_online"), value: EVENT_TYPE.ONLINE },
                          { label: t("join_offline"), value: EVENT_TYPE.OFFLINE },
                        ]}
                        selected={online}
                        handleSelected={setOnline}
                        labelSize={14}
                      />
                    </>
                  )}
                  {item.isApplicationEnable && (
                    <OutlinedButton
                      caption={t("request_join_behalf")}
                      onClick={() => handleOpenBehalfJoinDlg()}
                      sx={{ mt: "20px", mx: "auto", color: "primaryMain", width: "250px" }}
                      fontColor="primary.main"
                      fontWeight="700"
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              sx={{
                minWidth: "240px",
                py: "16px",
                px: "32px",
                fontSize: "1rem",
                fontFamily: "Noto Sans JP !important",
                lineHeight: "1rem",
                fontWeight: 700,
                mt: "20px",
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                borderRadius: 0,
                ":hover": {
                  bgcolor: "#2DA389",
                },
              }}
              onClick={() => {
                navigate("/signup/type")
              }}
            >
              {t("register_to_join")}
            </Button>
          )}
        </Box>
      </Modal>
      <EventBehalfJoinModal
        open={openBehalfDlg}
        handleOpen={setOpenBehalfDlg}
        item={item}
        joinType={online!}
        refreshEvents={refreshEvents}
      />
    </>
  )
}
